"use strict";

import {
  CircularProgress,
  Menu,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";

import { getCharacterLabel } from "./CharacterSelector.js";
import CharacterDeleteDialog from "./CharacterDeleteDialog.js";
import CharacterVoicesView from "./CharacterVoicesView.js";
import useRedoOffers from "./useRedoOffers.js";
import useVoiceRedoJobs from "./useVoiceRedoJobs.js";
import ImportCharactersPanel from "../ImportCharactersPanel.js";
import VoicePreviewRow from "./VoicePreviewRow.js";
import useVoiceCreation, {
  EMOTION_OPTIONS,
  TEXT_MAX_LENGTH,
  VOICE_DESCRIPTION_MAX_LENGTH,
} from "./useVoiceCreation.js";
import {
  CHARACTER_NAME_MAX_LENGTH,
  characterWriteErrorMessage,
  createCharacter,
  renameCharacter,
} from "./renameCharacter.js";
import { VOICE_CREATION_MODES } from "./voiceCreationModes.js";
import classes from "../../Assets/Styles/CharacterVoicePanel.module.css";

const PANEL_MODES = [VOICE_CREATION_MODES.remix, VOICE_CREATION_MODES.design];
const CUSTOM_EMOTION = "__custom";
const CUSTOM_EMOTION_MAX_LENGTH = 40;

const getInitials = (name) => {
  const parts = name.split(/[\s_-]+/).filter(Boolean);

  // Keep the whole number in names like "speaker_10", so it doesn't read
  // the same as "speaker_1".
  if (parts.length >= 2 && /^\d+$/.test(parts[1])) {
    return `${parts[0][0].toUpperCase()}${parts[1].slice(0, 2)}`;
  }

  return (
    parts
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("") || "?"
  );
};

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

// Light fills with a dark text shade of the same hue, so each character
// keeps a recognisable color.
const AVATAR_COLORS = [
  { background: "#dbeafe", color: "#1e3a8a" },
  { background: "#dcfce7", color: "#14532d" },
  { background: "#fef3c7", color: "#78350f" },
  { background: "#fce7f3", color: "#831843" },
  { background: "#ede9fe", color: "#4c1d95" },
  { background: "#ccfbf1", color: "#134e4a" },
  { background: "#ffedd5", color: "#7c2d12" },
  { background: "#e0e7ff", color: "#312e81" },
];

const getAvatarColor = (key) => {
  let hash = 0;
  for (const char of key || "") {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const CharacterAvatar = ({ name, colorKey }) => (
  <span
    className={classes["Avatar"]}
    style={getAvatarColor(colorKey || name)}
    aria-hidden="true"
  >
    {getInitials(name)}
  </span>
);

// Voice remix or voice design for one character, sized for the narrow side
// panel: prompt first, generation settings folded under "Advanced".
const CharacterVoiceForm = ({
  mode,
  character,
  characters,
  projectId,
  projectName,
  onModeChange,
  onBack,
  onVoiceAccepted,
  onGeneratingChange,
  onGenerationDone,
  languageCode,
  redoJobs,
}) => {
  const [remixSpeakers, setRemixSpeakers] = useState([character.speaker]);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Remix can run on several characters at once, each remixed on its own;
  // design works on this one only.
  const selectedCharacters = useMemo(() => {
    const speakers = mode.allowMultipleCharacters
      ? remixSpeakers
      : [character.speaker];
    return speakers
      .map((speaker) => characters.find((item) => item.speaker === speaker))
      .filter(Boolean);
  }, [
    character.speaker,
    characters,
    mode.allowMultipleCharacters,
    remixSpeakers,
  ]);

  // What the kept voice is saved as: a standard emotion or a custom name.
  const [saveAs, setSaveAs] = useState("neutral");
  const [customName, setCustomName] = useState("");
  const saveAsEmotion = saveAs === CUSTOM_EMOTION ? customName.trim() : saveAs;

  // Remix starts from an existing voice of each character, chosen by emotion:
  // only emotions every selected character has a voice for.
  const remixSourceEmotions = mode.allowMultipleCharacters
    ? selectedCharacters
        .map(
          (item) =>
            new Set(
              (item.voices || []).map((voice) => voice?.emotion).filter(Boolean)
            )
        )
        .reduce(
          (shared, emotions) =>
            shared === null
              ? [...emotions]
              : shared.filter((emotion) => emotions.has(emotion)),
          null
        ) || []
    : [];

  // Callbacks change every render; long runs call the latest ones.
  const onGeneratingChangeRef = useRef(onGeneratingChange);
  onGeneratingChangeRef.current = onGeneratingChange;
  const onGenerationDoneRef = useRef(onGenerationDone);
  onGenerationDoneRef.current = onGenerationDone;

  const getCharacterName = useCallback(
    (speaker) => {
      const match = characters.find((item) => item.speaker === speaker);
      return match ? getCharacterLabel(match) : speaker;
    },
    [characters]
  );

  // After a voice is added, offer to redo that character's lines with it.
  const redo = useRedoOffers({
    projectId,
    languageCode,
    redoJobs,
    getCharacterName,
  });

  // Cards for this form's characters.
  const redoSpeakers = [
    ...new Set([
      character.speaker,
      ...(mode.allowMultipleCharacters ? remixSpeakers : []),
      ...Object.keys(redo.offers),
    ]),
  ].filter(redo.hasCard);

  const {
    selectedEmotion,
    setSelectedEmotion,
    voiceDescription,
    setVoiceDescription,
    text,
    setText,
    autoGenerateText,
    setAutoGenerateText,
    loudness,
    setLoudness,
    guidanceScale,
    setGuidanceScale,
    promptStrengthPreset,
    setPromptStrengthPreset,
    voiceResults,
    acceptedVoiceIds,
    acceptingVoiceIds,
    isSubmitting,
    canSubmit,
    handleGenerateVoices,
    handleAcceptPreview,
  } = useVoiceCreation({
    title: mode.label,
    endpoint: mode.endpoint,
    projectId,
    projectName,
    selectedCharacters,
    allowMultipleCharacters: mode.allowMultipleCharacters,
    defaultVoiceDescription: mode.defaultVoiceDescription,
    onVoiceAccepted: (accepted) => {
      onVoiceAccepted?.(accepted);
      redo.offer(accepted.speakerId, {
        voiceId: accepted.generatedVoiceId,
        emotion: accepted.emotion,
      });
    },
  });

  const remixSourceKey = remixSourceEmotions.join(",");
  useEffect(() => {
    if (
      mode.allowMultipleCharacters &&
      remixSourceEmotions.length &&
      !remixSourceEmotions.includes(selectedEmotion)
    ) {
      setSelectedEmotion(
        remixSourceEmotions.includes("neutral")
          ? "neutral"
          : remixSourceEmotions[0]
      );
    }
  }, [remixSourceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const missingRemixSource =
    mode.allowMultipleCharacters && remixSourceEmotions.length === 0;
  const missingCustomName = saveAs === CUSTOM_EMOTION && !saveAsEmotion;
  const canGenerate = canSubmit && !missingRemixSource && !missingCustomName;

  // Tell the list which characters this form is generating for, captured when
  // generation starts so editing the selection meanwhile doesn't move it.
  // When it finishes with takes, the list marks them as ready to review.
  const generatingSpeakersRef = useRef(null);

  useEffect(() => {
    if (isSubmitting && !generatingSpeakersRef.current) {
      generatingSpeakersRef.current = selectedCharacters.map(
        (item) => item.speaker
      );
    }
    if (!isSubmitting && generatingSpeakersRef.current) {
      if (voiceResults.length > 0) {
        onGenerationDoneRef.current?.(generatingSpeakersRef.current);
      }
      generatingSpeakersRef.current = null;
    }

    onGeneratingChangeRef.current?.(generatingSpeakersRef.current || []);
  }, [isSubmitting]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => onGeneratingChangeRef.current?.([]), []);

  // Each card sits right above the take whose voice it's about, when that take
  // is among the takes on screen; otherwise it goes under the takes.
  const redoCardTakeIds = Object.fromEntries(
    redoSpeakers
      .map((speaker) => {
        const voiceId = redo.cardVoiceId(speaker);
        const onScreen = voiceResults.some(
          (result) =>
            (result.parent_speaker_id || character.speaker) === speaker &&
            (result.previews || []).some(
              (preview) => preview.generated_voice_id === voiceId
            )
        );
        return onScreen ? [speaker, voiceId] : null;
      })
      .filter(Boolean)
  );

  const characterName = getCharacterLabel(character);
  const addableCharacters = characters.filter(
    (item) => !remixSpeakers.includes(item.speaker)
  );

  return (
    <>
      <div className={classes["FormHeader"]}>
        <button
          type="button"
          className={classes["IconButton"]}
          onClick={onBack}
          aria-label="Back to characters"
        >
          <ArrowBackIcon fontSize="small" />
        </button>
        <CharacterAvatar name={characterName} colorKey={character.speaker} />
        <span className={classes["FormTitle"]}>{characterName}</span>
      </div>

      <div className={classes["Body"]}>
        <div
          className={classes["Segmented"]}
          role="radiogroup"
          aria-label="Voice mode"
        >
          {PANEL_MODES.map((item) => (
            <button
              key={item.value}
              type="button"
              role="radio"
              aria-checked={item.value === mode.value}
              className={`${classes["SegmentedOption"]} ${
                item.value === mode.value
                  ? classes["SegmentedOptionSelected"]
                  : ""
              }`}
              onClick={() => onModeChange(item.value)}
            >
              {item.shortLabel}
            </button>
          ))}
        </div>

        <p className={classes["Hint"]}>{mode.description}</p>

        {mode.allowMultipleCharacters && (
          <>
            <div className={classes["FieldLabel"]}>Characters to remix</div>
            <div className={classes["Chips"]}>
              {selectedCharacters.map((item) => (
                <span key={item.speaker} className={classes["Chip"]}>
                  {getCharacterLabel(item)}
                  {item.speaker !== character.speaker && (
                    <button
                      type="button"
                      className={classes["ChipRemove"]}
                      aria-label={`Remove ${getCharacterLabel(item)}`}
                      onClick={() =>
                        setRemixSpeakers((previous) =>
                          previous.filter((speaker) => speaker !== item.speaker)
                        )
                      }
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </button>
                  )}
                </span>
              ))}
              {addableCharacters.length > 0 && (
                <button
                  type="button"
                  className={`${classes["Chip"]} ${classes["ChipAdd"]}`}
                  onClick={(event) => setAddMenuAnchor(event.currentTarget)}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                  Add character
                </button>
              )}
              {addableCharacters.length > 1 && (
                <button
                  type="button"
                  className={`${classes["Chip"]} ${classes["ChipAdd"]}`}
                  onClick={() =>
                    setRemixSpeakers((previous) => [
                      ...previous,
                      ...addableCharacters.map((item) => item.speaker),
                    ])
                  }
                >
                  <PlaylistAddIcon sx={{ fontSize: 14 }} />
                  Add all ({addableCharacters.length})
                </button>
              )}
              {remixSpeakers.length > 1 && (
                <button
                  type="button"
                  className={`${classes["Chip"]} ${classes["ChipClear"]}`}
                  onClick={() => setRemixSpeakers([character.speaker])}
                >
                  Clear
                </button>
              )}
            </div>
            <p className={classes["Hint"]}>
              Each character gets its own new voice from the same prompt.
            </p>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
            >
              {addableCharacters.map((item) => (
                <MenuItem
                  key={item.speaker}
                  onClick={() => {
                    setRemixSpeakers((previous) => [...previous, item.speaker]);
                    setAddMenuAnchor(null);
                  }}
                >
                  {getCharacterLabel(item)}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <label className={classes["FieldLabel"]} htmlFor="voice-panel-prompt">
          Prompt
        </label>
        <TextField
          id="voice-panel-prompt"
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          size="small"
          value={voiceDescription}
          onChange={(event) => setVoiceDescription(event.target.value)}
          placeholder={mode.defaultVoiceDescription}
          inputProps={{ maxLength: VOICE_DESCRIPTION_MAX_LENGTH }}
          className={classes["TextInput"]}
        />
        <div className={classes["Counter"]}>
          {voiceDescription.length} / {VOICE_DESCRIPTION_MAX_LENGTH}
        </div>

        {mode.allowMultipleCharacters && (
          <>
            <label
              className={classes["FieldLabel"]}
              htmlFor={`voice-panel-source-${character.speaker}`}
            >
              Remix from
            </label>
            {missingRemixSource ? (
              <p className={classes["FieldWarning"]}>
                {selectedCharacters.length > 1
                  ? "These characters share no voice to remix. Remove one, or use Design."
                  : `${characterName} has no voice to remix yet. Use Design to create one.`}
              </p>
            ) : (
              <Select
                id={`voice-panel-source-${character.speaker}`}
                size="small"
                fullWidth
                value={selectedEmotion}
                onChange={(event) => setSelectedEmotion(event.target.value)}
                className={classes["SelectInput"]}
              >
                {remixSourceEmotions.map((emotion) => (
                  <MenuItem key={emotion} value={emotion}>
                    {capitalize(emotion)} voice
                  </MenuItem>
                ))}
              </Select>
            )}
          </>
        )}

        <label
          className={classes["FieldLabel"]}
          htmlFor={`voice-panel-save-as-${character.speaker}-${mode.value}`}
        >
          Save new voice as
        </label>
        <Select
          id={`voice-panel-save-as-${character.speaker}-${mode.value}`}
          size="small"
          fullWidth
          value={saveAs}
          onChange={(event) => setSaveAs(event.target.value)}
          className={classes["SelectInput"]}
        >
          {EMOTION_OPTIONS.map((emotion) => (
            <MenuItem key={emotion} value={emotion}>
              {capitalize(emotion)}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM_EMOTION}>Custom…</MenuItem>
        </Select>
        {saveAs === CUSTOM_EMOTION && (
          <input
            className={classes["CustomEmotionInput"]}
            value={customName}
            maxLength={CUSTOM_EMOTION_MAX_LENGTH}
            placeholder="Whispering, radio announcer…"
            aria-label="Custom name for the new voice"
            autoFocus
            onChange={(event) => setCustomName(event.target.value)}
          />
        )}

        <label className={classes["SwitchRow"]}>
          <span>Auto-generate preview text</span>
          <Switch
            size="small"
            checked={autoGenerateText}
            onChange={(event) => setAutoGenerateText(event.target.checked)}
          />
        </label>

        {!autoGenerateText && (
          <>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="Preview text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              inputProps={{ maxLength: TEXT_MAX_LENGTH }}
              className={classes["TextInput"]}
            />
            <div className={classes["Counter"]}>
              {text.length} / {TEXT_MAX_LENGTH} (at least 100)
            </div>
          </>
        )}

        <button
          type="button"
          className={classes["AdvancedToggle"]}
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced((shown) => !shown)}
        >
          Advanced
          <ExpandMoreIcon
            fontSize="small"
            className={showAdvanced ? classes["Rotated"] : ""}
          />
        </button>

        {showAdvanced && (
          <div className={classes["Advanced"]}>
            <label
              className={classes["FieldLabel"]}
              htmlFor="voice-panel-strength"
            >
              Prompt strength
            </label>
            <Select
              id="voice-panel-strength"
              size="small"
              fullWidth
              value={promptStrengthPreset}
              onChange={(event) => setPromptStrengthPreset(event.target.value)}
              className={classes["SelectInput"]}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>

            <div className={classes["FieldLabel"]}>Loudness: {loudness}</div>
            <Slider
              size="small"
              value={loudness}
              min={-1}
              max={1}
              step={0.1}
              onChange={(event, value) => setLoudness(Number(value))}
              aria-label="Loudness"
            />

            <div className={classes["FieldLabel"]}>
              Guidance scale: {guidanceScale}
            </div>
            <Slider
              size="small"
              value={guidanceScale}
              min={0}
              max={100}
              step={1}
              onChange={(event, value) => setGuidanceScale(Number(value))}
              aria-label="Guidance scale"
            />
          </div>
        )}

        {voiceResults.length > 0 && (
          <div className={classes["Results"]}>
            <div className={classes["FieldLabel"]}>Takes</div>
            {voiceResults.map((result, resultIndex) => {
              const resultSpeakerId =
                result.parent_speaker_id || character.speaker;
              const resultCharacter = characters.find(
                (item) => item.speaker === resultSpeakerId
              );
              const resultName = resultCharacter
                ? getCharacterLabel(resultCharacter)
                : resultSpeakerId;
              const previews = Array.isArray(result.previews)
                ? result.previews
                : [];

              return (
                <div
                  key={`${resultSpeakerId}-${resultIndex}`}
                  className={classes["ResultGroup"]}
                >
                  {voiceResults.length > 1 && (
                    <div className={classes["ResultTitle"]}>{resultName}</div>
                  )}
                  {previews.map((preview, previewIndex) => (
                    <React.Fragment
                      key={
                        preview.generated_voice_id ||
                        `${resultSpeakerId}-${previewIndex}`
                      }
                    >
                      {redoCardTakeIds[resultSpeakerId] &&
                        redoCardTakeIds[resultSpeakerId] ===
                          preview.generated_voice_id && (
                          <div className={classes["RedoCardAboveTake"]}>
                            {redo.renderCard(resultSpeakerId)}
                          </div>
                        )}
                      <VoicePreviewRow
                        preview={preview}
                        characterName={resultName}
                        index={previewIndex}
                        speakerId={resultSpeakerId}
                        emotion={saveAsEmotion}
                        isAccepted={acceptedVoiceIds.includes(
                          preview.generated_voice_id
                        )}
                        isAccepting={acceptingVoiceIds.includes(
                          preview.generated_voice_id
                        )}
                        onAccept={handleAcceptPreview}
                      />
                    </React.Fragment>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {redoSpeakers
          .filter((speaker) => !redoCardTakeIds[speaker])
          .map(redo.renderCard)}
      </div>

      <div className={classes["Footer"]}>
        <button
          type="button"
          className={classes["GenerateButton"]}
          onClick={handleGenerateVoices}
          disabled={!canGenerate}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <AutoFixHighIcon
              fontSize="small"
              className={classes["GenerateIcon"]}
            />
          )}
          {isSubmitting ? "Generating…" : "Generate voices"}
        </button>
      </div>
    </>
  );
};

// Inline name field replacing a character row (or the voices view's title)
// while it's being renamed. Enter saves, Escape cancels.
const CharacterRenameRow = ({
  character,
  projectId,
  onRenamed,
  onCancel,
  className = "",
  showAvatar = true,
}) => {
  const [draftName, setDraftName] = useState(getCharacterLabel(character));
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Start with the current name selected, ready to be typed over.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = async () => {
    const nextName = draftName.trim();

    if (!nextName) {
      toast.error("Character name cannot be empty.");
      return;
    }

    if (nextName === getCharacterLabel(character)) {
      onCancel();
      return;
    }

    try {
      setIsSaving(true);
      const renamed = await renameCharacter({
        projectId,
        speaker: character.speaker,
        name: nextName,
      });

      if (!renamed) {
        toast.error("Character no longer exists on this project.");
        return;
      }

      onRenamed(character.speaker, nextName);
      toast.success(`Renamed to "${nextName}".`);
    } catch (error) {
      console.error("Failed to rename character:", error);
      toast.error(
        characterWriteErrorMessage(error, "Error updating character name")
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`${classes["RenameRow"]} ${className}`}>
      {showAvatar && (
        <CharacterAvatar
          name={draftName || getCharacterLabel(character)}
          colorKey={character.speaker}
        />
      )}
      <input
        className={classes["RenameInput"]}
        value={draftName}
        maxLength={CHARACTER_NAME_MAX_LENGTH}
        aria-label={`New name for ${getCharacterLabel(character)}`}
        disabled={isSaving}
        ref={inputRef}
        onChange={(event) => setDraftName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
          if (event.key === "Escape") onCancel();
        }}
      />
      {isSaving ? (
        <CircularProgress size={18} sx={{ mx: "6px" }} />
      ) : (
        <>
          <button
            type="button"
            className={classes["IconButton"]}
            aria-label="Save name"
            onClick={save}
          >
            <CheckIcon fontSize="small" />
          </button>
          <button
            type="button"
            className={classes["IconButton"]}
            aria-label="Cancel renaming"
            onClick={onCancel}
          >
            <CloseIcon fontSize="small" />
          </button>
        </>
      )}
    </div>
  );
};

// Inline name field for a new character, shown at the top of the list.
// Enter creates it, Escape cancels.
const CharacterCreateRow = ({ projectId, characters, onCreated, onCancel }) => {
  const [draftName, setDraftName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    const name = draftName.trim();

    if (!name) {
      toast.error("Character name cannot be empty.");
      return;
    }

    if (
      characters.some(
        (item) => getCharacterLabel(item).toLowerCase() === name.toLowerCase()
      )
    ) {
      toast.error(`A character named "${name}" already exists.`);
      return;
    }

    try {
      setIsSaving(true);
      const character = await createCharacter({ projectId, name });
      toast.success(`Created "${name}".`);
      await onCreated(character);
    } catch (error) {
      console.error("Failed to create character:", error);
      toast.error(characterWriteErrorMessage(error, "Error creating character"));
      setIsSaving(false);
    }
  };

  return (
    <div className={`${classes["RenameRow"]} ${classes["CreateRow"]}`}>
      <CharacterAvatar name={draftName.trim() || "?"} colorKey={draftName} />
      <input
        className={classes["RenameInput"]}
        value={draftName}
        maxLength={CHARACTER_NAME_MAX_LENGTH}
        placeholder="New character name"
        aria-label="New character name"
        disabled={isSaving}
        autoFocus
        onChange={(event) => setDraftName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
          if (event.key === "Escape") onCancel();
        }}
      />
      {isSaving ? (
        <CircularProgress size={18} sx={{ mx: "6px" }} />
      ) : (
        <>
          <button
            type="button"
            className={classes["IconButton"]}
            aria-label="Create character"
            onClick={save}
          >
            <CheckIcon fontSize="small" />
          </button>
          <button
            type="button"
            className={classes["IconButton"]}
            aria-label="Cancel creating character"
            onClick={onCancel}
          >
            <CloseIcon fontSize="small" />
          </button>
        </>
      )}
    </div>
  );
};

/**
 * Characters list for a side panel; picking a character opens voice remix or
 * voice design for it in place, with a back button to the list.
 */
const CharacterVoicePanel = ({
  projectId,
  projectName,
  characters,
  isLoading = false,
  onVoiceAccepted,
  onCharacterRenamed,
  onCharactersImported,
  onCharacterCreated,
  onCharacterDeleted,
  onActivityChange,
  isVisible = true,
  languageCode,
  onLinesRegenerated,
}) => {
  // null shows the list; otherwise { speaker, mode } shows that form.
  const [editing, setEditing] = useState(null);
  // Every form opened so far, as { speaker, mode }. They stay mounted while
  // hidden, so going back to the list or switching between Remix and Design
  // keeps each one's prompt, takes and any generation in progress.
  const [openedForms, setOpenedForms] = useState([]);
  const [renamingSpeaker, setRenamingSpeaker] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // Character whose trained voices are shown, or null.
  const [voicesSpeaker, setVoicesSpeaker] = useState(null);
  // Whether the voices view's title is being renamed.
  const [isRenamingInVoices, setIsRenamingInVoices] = useState(false);
  // Row whose More menu is open, as { anchor, speaker }, or null.
  const [rowMenu, setRowMenu] = useState(null);
  // Only a keyboard open moves focus into the menu and back to the button on
  // close; with a mouse that would leave Rename and the row looking selected.
  // Kept in a ref so it outlasts rowMenu while the menu closes.
  const rowMenuViaKeyboardRef = useRef(false);
  // Character waiting for delete confirmation, or null.
  const [deletingCharacter, setDeletingCharacter] = useState(null);
  // Characters added by the last import, highlighted for a few seconds.
  const [newSpeakers, setNewSpeakers] = useState([]);
  const speakersBeforeImportRef = useRef(null);
  // Speakers each form is generating for, keyed by "speaker-mode".
  const [generatingByForm, setGeneratingByForm] = useState({});

  const handleFormGenerating = useCallback((formKey, speakers) => {
    setGeneratingByForm((previous) => {
      if (!speakers.length && !previous[formKey]) return previous;
      const next = { ...previous };
      if (speakers.length) next[formKey] = speakers;
      else delete next[formKey];
      return next;
    });
  }, []);

  // Speakers with finished takes not yet looked at, keyed like
  // generatingByForm; cleared once that form is viewed.
  const [readyByForm, setReadyByForm] = useState({});
  const activeFormKey =
    editing && isVisible ? `${editing.speaker}-${editing.mode}` : null;
  const activeFormKeyRef = useRef(activeFormKey);
  activeFormKeyRef.current = activeFormKey;

  const handleFormDone = useCallback((formKey, speakers) => {
    // Already on screen, so there's nothing to point out.
    if (activeFormKeyRef.current === formKey) return;
    setReadyByForm((previous) => ({ ...previous, [formKey]: speakers }));
  }, []);

  useEffect(() => {
    if (!activeFormKey) return;
    const redoKey = `redo:${editing.speaker}`;
    setReadyByForm((previous) => {
      if (!previous[activeFormKey] && !previous[redoKey]) return previous;
      const next = { ...previous };
      delete next[activeFormKey];
      delete next[redoKey];
      return next;
    });
  }, [activeFormKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side runs redoing a character's lines with a new voice.
  const onLinesRegeneratedRef = useRef(onLinesRegenerated);
  onLinesRegeneratedRef.current = onLinesRegenerated;
  const handleRedoFinished = useCallback(
    (job) => {
      const match = (characters || []).find(
        (item) => item.speaker === job.speakerId
      );
      const name = match ? getCharacterLabel(match) : job.speakerId;
      if (job.status === "completed" && !job.failed) {
        toast.success(
          `Redid all ${job.total} of ${name}'s lines with the new voice.`
        );
      } else if (job.status === "completed") {
        toast.warning(
          `Redid ${job.succeeded} of ${job.total} of ${name}'s lines. ${job.failed} failed.`
        );
      } else if (job.status === "cancelled") {
        toast.info(`Stopped redoing ${name}'s lines.`);
      } else {
        toast.error(job.error?.title || `Redoing ${name}'s lines failed.`);
      }
      onLinesRegeneratedRef.current?.(job.speakerId);
      handleFormDone(`redo:${job.speakerId}`, [job.speakerId]);
    },
    [characters] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const redoJobs = useVoiceRedoJobs({
    projectId,
    languageCode,
    onJobFinished: handleRedoFinished,
  });

  const generatingSpeakers = useMemo(
    () =>
      new Set([
        ...Object.values(generatingByForm).flat(),
        ...redoJobs.runningSpeakers,
      ]),
    [generatingByForm, redoJobs.runningSpeakers]
  );

  const readySpeakers = useMemo(
    () => new Set(Object.values(readyByForm).flat()),
    [readyByForm]
  );

  useEffect(() => {
    onActivityChange?.({
      generating: generatingSpeakers.size > 0,
      ready: readySpeakers.size > 0,
    });
  }, [generatingSpeakers, readySpeakers, onActivityChange]);

  // A marked character opens the form holding its takes or its generation.
  const getFormKeyForSpeaker = (speaker) =>
    Object.keys(readyByForm).find((key) =>
      readyByForm[key].includes(speaker)
    ) ||
    Object.keys(generatingByForm).find((key) =>
      generatingByForm[key].includes(speaker)
    );

  const openForm = (speaker, mode) => {
    setEditing({ speaker, mode });
    setOpenedForms((previous) =>
      previous.some((form) => form.speaker === speaker && form.mode === mode)
        ? previous
        : [...previous, { speaker, mode }]
    );
  };

  const sortedCharacters = useMemo(
    () =>
      (characters || [])
        .filter((character) => character.isUsed !== false)
        .sort((a, b) =>
          getCharacterLabel(a).localeCompare(getCharacterLabel(b))
        ),
    [characters]
  );

  const editingCharacter = editing
    ? sortedCharacters.find(
        (character) => character.speaker === editing.speaker
      )
    : null;
  const showingList = !editingCharacter;
  const voicesCharacter = voicesSpeaker
    ? sortedCharacters.find((character) => character.speaker === voicesSpeaker)
    : null;

  const handleDeleted = async (speaker) => {
    if (voicesSpeaker === speaker) {
      setVoicesSpeaker(null);
      setIsRenamingInVoices(false);
    }
    if (editing?.speaker === speaker) setEditing(null);
    await onCharacterDeleted?.(speaker);
  };

  const rowMenuCharacter = rowMenu
    ? sortedCharacters.find((character) => character.speaker === rowMenu.speaker)
    : null;

  const handleImported = async () => {
    speakersBeforeImportRef.current = new Set(
      sortedCharacters.map((character) => character.speaker)
    );
    await onCharactersImported?.();
    setShowImport(false);
  };

  const handleCreated = async () => {
    speakersBeforeImportRef.current = new Set(
      sortedCharacters.map((character) => character.speaker)
    );
    await onCharacterCreated?.();
    setIsCreating(false);
  };

  const createRow = isCreating ? (
    <CharacterCreateRow
      projectId={projectId}
      characters={sortedCharacters}
      onCreated={handleCreated}
      onCancel={() => setIsCreating(false)}
    />
  ) : null;

  // Once the reloaded list arrives, highlight whatever the import or
  // creation added.
  useEffect(() => {
    const before = speakersBeforeImportRef.current;
    if (!before) return undefined;
    speakersBeforeImportRef.current = null;

    const added = sortedCharacters
      .map((character) => character.speaker)
      .filter((speaker) => !before.has(speaker));
    if (!added.length) return undefined;

    setNewSpeakers(added);
    const timer = setTimeout(() => setNewSpeakers([]), 6000);
    return () => clearTimeout(timer);
  }, [sortedCharacters]);

  return (
    <>
      {openedForms.map((form) => {
        const formCharacter = sortedCharacters.find(
          (character) => character.speaker === form.speaker
        );
        if (!formCharacter) return null;

        const isActive =
          !showingList &&
          editing.speaker === form.speaker &&
          editing.mode === form.mode;

        return (
          <div
            key={`${form.speaker}-${form.mode}`}
            className={classes["Pane"]}
            hidden={!isActive}
          >
            <CharacterVoiceForm
              mode={VOICE_CREATION_MODES[form.mode]}
              character={formCharacter}
              characters={sortedCharacters}
              projectId={projectId}
              projectName={projectName}
              onModeChange={(modeValue) => openForm(form.speaker, modeValue)}
              onBack={() => setEditing(null)}
              onVoiceAccepted={onVoiceAccepted}
              onGeneratingChange={(speakers) =>
                handleFormGenerating(`${form.speaker}-${form.mode}`, speakers)
              }
              onGenerationDone={(speakers) =>
                handleFormDone(`${form.speaker}-${form.mode}`, speakers)
              }
              languageCode={languageCode}
              redoJobs={redoJobs}
            />
          </div>
        );
      })}

      <div className={classes["Pane"]} hidden={!(showingList && showImport)}>
        <div className={classes["FormHeader"]}>
          <button
            type="button"
            className={classes["IconButton"]}
            onClick={() => setShowImport(false)}
            aria-label="Back to characters"
          >
            <ArrowBackIcon fontSize="small" />
          </button>
          <span className={classes["FormTitle"]}>Import cast</span>
        </div>
        <div className={classes["Body"]}>
          <p className={classes["Hint"]} style={{ marginTop: 0 }}>
            Copy every character from another project into this one. Characters
            already here are kept, and duplicates are skipped.
          </p>
          <div className={classes["ImportSearch"]}>
            <ImportCharactersPanel
              projectId={projectId}
              onImported={handleImported}
              stacked
            />
          </div>
        </div>
      </div>

      {voicesCharacter && (
        <div className={classes["Pane"]} hidden={!showingList || showImport}>
          <CharacterVoicesView
            character={voicesCharacter}
            avatar={
              <CharacterAvatar
                name={getCharacterLabel(voicesCharacter)}
                colorKey={voicesCharacter.speaker}
              />
            }
            projectId={projectId}
            languageCode={languageCode}
            redoJobs={redoJobs}
            onBack={() => {
              setIsRenamingInVoices(false);
              setVoicesSpeaker(null);
            }}
            onOpenForm={(mode) => openForm(voicesCharacter.speaker, mode)}
            onRename={() => setIsRenamingInVoices(true)}
            renameField={
              isRenamingInVoices ? (
                <CharacterRenameRow
                  character={voicesCharacter}
                  projectId={projectId}
                  className={classes["RenameRowInHeader"]}
                  showAvatar={false}
                  onRenamed={(speaker, displayName) => {
                    onCharacterRenamed?.(speaker, displayName);
                    setIsRenamingInVoices(false);
                  }}
                  onCancel={() => setIsRenamingInVoices(false)}
                />
              ) : null
            }
            onDelete={() => setDeletingCharacter(voicesCharacter)}
            deleteDisabledReason={
              generatingSpeakers.has(voicesCharacter.speaker)
                ? "Wait for this character's voices to finish generating"
                : null
            }
          />
        </div>
      )}

      <div
        className={classes["Pane"]}
        hidden={!showingList || showImport || Boolean(voicesCharacter)}
      >
        {sortedCharacters.length > 0 && (
          <div className={classes["ListHeader"]}>
            <span>
              {sortedCharacters.length}{" "}
              {sortedCharacters.length === 1 ? "character" : "characters"}
            </span>
            <span className={classes["HeaderButtons"]}>
              <button
                type="button"
                className={classes["HeaderButton"]}
                onClick={() => setIsCreating(true)}
                disabled={isCreating}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Create
              </button>
              <button
                type="button"
                className={classes["HeaderButton"]}
                onClick={() => setShowImport(true)}
              >
                <GroupAddOutlinedIcon sx={{ fontSize: 16 }} />
                Import
              </button>
            </span>
          </div>
        )}
        <div className={classes["Body"]} style={{ padding: 0 }}>
          {isLoading && sortedCharacters.length === 0 ? (
            <p className={classes["Hint"]} style={{ padding: 12 }}>
              Loading characters…
            </p>
          ) : sortedCharacters.length === 0 ? (
            isCreating ? (
              createRow
            ) : (
              <div className={classes["EmptyState"]}>
                <p>This project has no characters yet.</p>
                <button
                  type="button"
                  className={classes["EmptyStateButton"]}
                  onClick={() => setIsCreating(true)}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                  Create character
                </button>
                <button
                  type="button"
                  className={classes["EmptyStateButton"]}
                  onClick={() => setShowImport(true)}
                >
                  <GroupAddOutlinedIcon sx={{ fontSize: 18 }} />
                  Import cast
                </button>
              </div>
            )
          ) : (
            <ul className={classes["List"]}>
              {isCreating && <li className={classes["Row"]}>{createRow}</li>}
              {sortedCharacters.map((character) => {
                const name = getCharacterLabel(character);
                const voiceCount = character.voices?.length || 0;
                const isGenerating = generatingSpeakers.has(character.speaker);
                const hasReadyTakes =
                  !isGenerating && readySpeakers.has(character.speaker);
                const isNew = newSpeakers.includes(character.speaker);

                if (renamingSpeaker === character.speaker) {
                  return (
                    <li key={character.speaker} className={classes["Row"]}>
                      <CharacterRenameRow
                        character={character}
                        projectId={projectId}
                        onRenamed={(speaker, displayName) => {
                          onCharacterRenamed?.(speaker, displayName);
                          setRenamingSpeaker(null);
                        }}
                        onCancel={() => setRenamingSpeaker(null)}
                      />
                    </li>
                  );
                }

                return (
                  <li
                    key={character.speaker}
                    className={`${classes["Row"]} ${
                      isNew ? classes["RowNew"] : ""
                    }`}
                  >
                    <button
                      type="button"
                      className={classes["RowMain"]}
                      onClick={() => {
                        // A character with takes waiting or a generation
                        // running opens the form holding them.
                        const formKey = getFormKeyForSpeaker(character.speaker);
                        const form = formKey
                          ? openedForms.find(
                              (item) =>
                                `${item.speaker}-${item.mode}` === formKey
                            )
                          : null;
                        if (form) openForm(form.speaker, form.mode);
                        else setVoicesSpeaker(character.speaker);
                      }}
                    >
                      <span className={classes["AvatarWrap"]}>
                        <CharacterAvatar
                          name={name}
                          colorKey={character.speaker}
                        />
                        {isGenerating && (
                          <span
                            className={classes["GeneratingBadge"]}
                            aria-hidden="true"
                          />
                        )}
                        {hasReadyTakes && (
                          <span
                            className={classes["ReadyBadge"]}
                            aria-hidden="true"
                          />
                        )}
                      </span>
                      <span className={classes["RowText"]}>
                        <span className={classes["RowName"]} title={name}>
                          {name}
                          {isNew && (
                            <span className={classes["NewChip"]}>New</span>
                          )}
                        </span>
                        {isGenerating ? (
                          <span className={classes["RowMetaGenerating"]}>
                            Generating…
                          </span>
                        ) : hasReadyTakes ? (
                          <span className={classes["RowMetaReady"]}>
                            Takes ready
                          </span>
                        ) : (
                          <span className={classes["RowMeta"]}>
                            {voiceCount} {voiceCount === 1 ? "voice" : "voices"}
                          </span>
                        )}
                      </span>
                    </button>

                    <span className={classes["RowActions"]}>
                      <Tooltip title="Voices">
                        <button
                          type="button"
                          className={classes["IconButton"]}
                          aria-label={`${name}'s voices`}
                          onClick={() => setVoicesSpeaker(character.speaker)}
                        >
                          <RecordVoiceOverOutlinedIcon fontSize="small" />
                        </button>
                      </Tooltip>
                      {PANEL_MODES.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Tooltip key={item.value} title={item.label}>
                            <button
                              type="button"
                              className={classes["IconButton"]}
                              aria-label={`${item.label} for ${name}`}
                              onClick={() =>
                                openForm(character.speaker, item.value)
                              }
                            >
                              <Icon fontSize="small" />
                            </button>
                          </Tooltip>
                        );
                      })}
                      <Tooltip title="More">
                        <button
                          type="button"
                          className={classes["IconButton"]}
                          aria-label={`More actions for ${name}`}
                          aria-haspopup="menu"
                          aria-expanded={rowMenu?.speaker === character.speaker}
                          onClick={(event) => {
                            // Enter and Space clicks report no click count.
                            rowMenuViaKeyboardRef.current = event.detail === 0;
                            setRowMenu({
                              anchor: event.currentTarget,
                              speaker: character.speaker,
                            });
                          }}
                        >
                          <MoreHorizIcon fontSize="small" />
                        </button>
                      </Tooltip>
                    </span>
                  </li>
                );
              })}
              <li>
                <button
                  type="button"
                  className={classes["ImportRow"]}
                  onClick={() => setShowImport(true)}
                >
                  <span className={classes["ImportRowIcon"]} aria-hidden="true">
                    <GroupAddOutlinedIcon sx={{ fontSize: 16 }} />
                  </span>
                  <span className={classes["RowText"]}>
                    <span className={classes["ImportRowTitle"]}>
                      Import cast
                    </span>
                    <span className={classes["RowMeta"]}>
                      From another project
                    </span>
                  </span>
                </button>
              </li>
            </ul>
          )}
        </div>

        <Menu
          anchorEl={rowMenu?.anchor}
          open={Boolean(rowMenuCharacter)}
          onClose={() => setRowMenu(null)}
          MenuListProps={{ autoFocusItem: rowMenuViaKeyboardRef.current }}
          disableRestoreFocus={!rowMenuViaKeyboardRef.current}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ className: classes["RowMenuPaper"] }}
        >
          <MenuItem
            className={classes["RowMenuItem"]}
            onClick={() => {
              setRenamingSpeaker(rowMenu.speaker);
              setRowMenu(null);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
            Rename
          </MenuItem>
          <li className={classes["RowMenuDivider"]} role="separator" />
          {rowMenuCharacter && generatingSpeakers.has(rowMenuCharacter.speaker) ? (
            <Tooltip
              title="Wait for this character's voices to finish generating"
              placement="left"
            >
              <span>
                <MenuItem
                  disabled
                  className={`${classes["RowMenuItem"]} ${classes["RowMenuItemDanger"]}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                  Delete character
                </MenuItem>
              </span>
            </Tooltip>
          ) : (
            <MenuItem
              className={`${classes["RowMenuItem"]} ${classes["RowMenuItemDanger"]}`}
              onClick={() => {
                setDeletingCharacter(rowMenuCharacter);
                setRowMenu(null);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
              Delete character
            </MenuItem>
          )}
        </Menu>

        <CharacterDeleteDialog
          character={deletingCharacter}
          projectId={projectId}
          onClose={() => setDeletingCharacter(null)}
          onDeleted={handleDeleted}
        />

        <a
          className={classes["FooterLink"]}
          href={`/projects/${projectId}/character-customization`}
          target="_blank"
          rel="noreferrer"
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
          Open full casting
        </a>
      </div>
    </>
  );
};

export default CharacterVoicePanel;
